import { useEffect, lazy, Suspense } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import MobileStickyCTA from "./MobileStickyCTA";
import { WaitlistCTAProvider } from "../lib/useWaitlistCTA";
import { captureUtmsOnFirstTouch } from "../lib/utm";
import { trackEvent } from "../lib/analytics";

// DemoPill only appears after the visitor scrolls past the hero (~600px).
// Lazy-load so the floating "Watch the demo" UI and its modal contents
// (which include AnimatedChatDemo) do not block initial render.
const DemoPill = lazy(() => import("./DemoPill"));

const META = {
    "/": {
        title: "TopParent - Home",
        description:
            "TopParent helps parents find simple, low-prep play ideas that fit their child, the moment, and everyday home routines. Join the waitlist.",
    },
    "/waitlist": {
        title: "TopParent - Home",
        description:
            "TopParent helps parents find simple, low-prep play ideas that fit their child, the moment, and everyday home routines. Join the waitlist.",
    },
    "/how-it-works": {
        title: "TopParent - How it works",
        description:
            "Simple support for real home routines. TopParent starts with a few simple questions, then suggests one low-prep activity that fits your time, materials, and routine.",
    },
    "/neurodiverse-play": {
        title: "TopParent - Children who learn differently",
        description:
            "For children who learn differently, the environment matters. TopParent is being shaped for families who need calmer, clearer, and easier-to-adapt activity ideas.",
    },
    "/why": {
        title: "TopParent - Why we are building TopParent",
        description:
            "TopSchool was built around a simple belief: every child learns differently. TopParent brings that belief into the home, in small everyday moments.",
    },
    "/thank-you": {
        title: "TopParent - Thank you",
        description:
            "Thank you for joining TopParent. When the WhatsApp experience is ready, we will email you with the next step.",
    },
    "/referral": {
        title: "TopParent - Share TopParent with another parent",
        description:
            "Share TopParent with another parent who may want simple, low-prep activity ideas at home. Track your private invite link and progress.",
    },
    "/privacy": {
        title: "TopParent - Privacy",
        description:
            "How TopParent collects, uses, and protects your information during our pre-launch waitlist.",
    },
};

const Layout = () => {
    const { pathname } = useLocation();

    // META is a module constant, and `meta`/`tag` are local to the effect.
    // Only the pathname needs to trigger a re-run.
    useEffect(() => {
        const meta = META[pathname] || META["/"];
        document.title = meta.title;
        let tag = document.querySelector('meta[name="description"]');
        if (!tag) {
            tag = document.createElement("meta");
            tag.setAttribute("name", "description");
            document.head.appendChild(tag);
        }
        tag.setAttribute("content", meta.description);
    }, [pathname]);

    // Capture first-touch UTM parameters into sessionStorage on first
    // page load. Subsequent client-side navigations re-run this but the
    // helper is idempotent: first-touch values are preserved.
    useEffect(() => {
        captureUtmsOnFirstTouch();
    }, [pathname]);

    // Inject the KickoffLabs AnyForm script once on first mount. This
    // component only mounts inside the primary pre-launch site (routes
    // wrapped by Layout), so the paid-acquisition routes under
    // /parent-activity/* — which sit outside Layout — never trigger
    // this loader and stay KickoffLabs-free.
    useEffect(() => {
        if (typeof document === "undefined") return;
        if (document.getElementById("koljs")) return;
        const s = document.createElement("script");
        s.id = "koljs";
        s.src = "https://b.kickoffpages.com/2.2.0/kol.js";
        s.async = true;
        s.setAttribute("data-campaign-id", "194315");
        document.body.appendChild(s);
    }, []);

    // SPA pageview tracking. Neither GA4's gtag('config') nor PostHog's
    // autocapture fire pageviews on client-side route changes, so we
    // dispatch one manually on every pathname change. Both analytics
    // backends are intentionally fed the same event shape via trackEvent.
    useEffect(() => {
        trackEvent("page_view", {
            page_path: pathname,
            page_title: (META[pathname] || META["/"]).title,
            page_location: typeof window !== "undefined" ? window.location.href : "",
        });
    }, [pathname]);

    return (
        <div className="min-h-screen flex flex-col bg-brand-offWhite">
            <WaitlistCTAProvider>
                <Header />
                <main className="flex-1">
                    <Outlet />
                </main>
                <Footer />
                <MobileStickyCTA />
                <Suspense fallback={null}>
                    <DemoPill />
                </Suspense>
            </WaitlistCTAProvider>
        </div>
    );
};

export default Layout;
